import {
    regionalSiteConfig,
    TheWestRegionalSections,
    TheWestSection,
} from '@news-mono/common'
import {
    adPaddingOptions,
    getCoralStoryId,
    getInlineEventAds,
    inlineArticleAds,
    metrics,
    RegionalReaderSurvey,
    teadsAdSlotID,
} from '@news-mono/component-library'
import { getElectionToggles } from '@news-mono/common'
import {
    AdTargeting,
    BaseClientConfig,
    ContentDataTypes,
    ElectionDefinition,
    GetRouteAdTargeting,
    isElectionFeatureEnabled,
    PageType,
    Product,
    publicationAdKind,
    publicationAdPageType,
    RenderTarget,
    TogglesReduxState,
} from '@news-mono/web-common'
import {
    ArticleLikeV4DTO,
    isWestRegionalSection,
    PageMetaDTO,
} from '@west-australian-newspapers/publication-types'
import { layout } from '../../../App.routing'
import { getAmpArticleComposition } from './article.amp'
import { getNoIndexMeta } from './allowList'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { getUnloadedElectionDefinition } from '../../elections/routes'
import { tokens } from '@news-mono/design-tokens'

// Used for Quicklinks
export const publicationPageType = 'publication'

export const createArticleRoute = ({
    article,
    config,
    section,
    getAdTargeting,
    pageMeta,
    renderTarget,
    toggles,
}: {
    article: ArticleLikeV4DTO
    config: BaseClientConfig
    section: TheWestSection
    getAdTargeting: GetRouteAdTargeting
    renderTarget: RenderTarget
    pageMeta?: PageMetaDTO
    toggles?: TogglesReduxState
}): PageType<TheWestSection> => {
    const featureState = toggles && toFeatureState(toggles)

    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        publicationAdKind[article.kind],
        section,
        article.topics.primary,
        ...article.topics.secondary,
    )

    const adTargeting: AdTargeting = {
        pageId: article.slug,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    // Used to keep track of the promo card options setup on the west desk
    const westPromotionCardTopics = [
        'news',
        'sport',
        'business',
        'politics',
        'lifestyle',
        'entertainment',
        'travel',
        'technology',
        'features',
        'opinion',
    ]

    const primaryTopicId = article.topics.primary.parent
        ? article.topics.primary.parent.id
        : article.topics.primary.id

    const promoCardMetadataKey = `${
        westPromotionCardTopics.includes(primaryTopicId)
            ? primaryTopicId
            : 'news'
    }-promotional-card`

    const isRetireReady = article.topics.primary.id.includes('retire-ready')

    const enableExtraInlineArticleAds = true

    // One generalised feature state checker
    const isFeature = (feature: string | undefined) =>
        (featureState &&
            feature !== undefined &&
            isFeatureEnabled(featureState, feature, false)) ??
        false
    const isElectionFeature = (
        feature: keyof ReturnType<typeof getElectionToggles> | undefined,
    ) =>
        (featureState &&
            feature !== undefined &&
            isElectionFeatureEnabled(
                toggles,
                electionDefinition.electionId,
                feature,
            )) ??
        false
    const getFeatureValue = (feature: string | undefined) =>
        (featureState &&
            feature !== undefined &&
            featureState[feature]?.value.string()) ??
        undefined

    /** Disable Ads on Breaches */
    const disableBreachAds =
        isFeature('breach-disable-ads') &&
        article.requiredAccess.level === 'subscriber'

    const enableTheGameDriver =
        article.topics.primary.id.includes('sport') &&
        article?.classification?.kind !== 'sales' &&
        isFeature('the-game-article-driver')

    const enableTeadsAds = isFeature('teads-article-ads')
    const enablePollyRaterBanner = isFeature('pollie-rater-drivers')

    const isFootyLiveHereBreachEnabled = isFeature(
        'afl-footy-lives-here-breach-screen',
    )
    const isGuernseyEnabled = isFeature(
        'afl-footy-lives-here-guernsey-earlybird-promo',
    )
    // Conditionally hides the article aside for the AFL Footy Lives Here breach screen
    // Authentication based conditions moved to Athena.tsx to ensure they are reactive to state changes
    const getShouldHideArticleSidebar = () => {
        if (article.requiredAccess.level === 'subscriber') {
            if (isGuernseyEnabled && isFootyLiveHereBreachEnabled) {
                // Sidebar hidden on all topics when the Footy Lives Here breach screen and guernsey promo are enabled
                return true
            } else if (isFootyLiveHereBreachEnabled) {
                // Sidebar hidden on sports topics, when only the Footy Lives Here breach screen is enabled
                return article.topics.primary.id.includes('sport')
            }
        }
        // Always show the sidebar for non-subscriber articles (no breach screens)
        return false
    }

    // Election Content
    const wanElectionId =
        getFeatureValue('article-header-wan-election-id') || 'wa-election-2025'
    const electionDefinition = getUnloadedElectionDefinition(wanElectionId)

    const enableElectionNavigation = isElectionFeature('election-navigation')
    const enableElectionSectionHeadToHead = isElectionFeature(
        'election-section-head-to-head',
    )

    const enableElectionSectionHeadToHeadCountdown = isElectionFeature(
        'election-head-to-head-countdown',
    )

    const enableNightlyPromoBanner =
        !isRetireReady && isFeature('tn-promo-banner')
    const enableOlympicsMedalTallyWidget =
        article.topics.primary.id.includes('sport') &&
        isFeature('paris-olympics-widgets')

    const extraInlineArticleAds = [
        inlineArticleAds({
            insertAfter: 3,
            noticePosition: 'below-center',
            hiddenUntilLoaded: false,
            mastHead: 'thewest',
            slotID: 'article-mrec-one',
            size: enableExtraInlineArticleAds ? 'alwaysMrec' : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
        }),
        inlineArticleAds({
            insertAfter: 12,
            noticePosition: 'below-center',
            hiddenUntilLoaded: true,
            mastHead: 'thewest',
            slotID: 'article-mrec-two',
            size: enableExtraInlineArticleAds ? 'alwaysMrec' : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
        }),
        inlineArticleAds({
            insertAfter: 21,
            noticePosition: 'below-center',
            hiddenUntilLoaded: true,
            mastHead: 'thewest',
            slotID: 'article-mrec-three',
            size: enableExtraInlineArticleAds ? 'alwaysMrec' : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
        }),
        inlineArticleAds({
            insertAfter: 30,
            noticePosition: 'below-center',
            hiddenUntilLoaded: true,
            mastHead: 'thewest',
            slotID: 'article-mrec-four',
            size: enableExtraInlineArticleAds ? 'alwaysMrec' : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
        }),

        ...(enableTeadsAds
            ? [
                  inlineArticleAds({
                      insertAfter: 5,
                      noticePosition: 'below-center',
                      hiddenUntilLoaded: false,
                      mastHead: 'thewest',
                      slotID: teadsAdSlotID,
                      size: 'teads',
                      pageType: publicationAdPageType[article.kind],
                  }),
              ]
            : []),
    ]

    return {
        kind: 'page',
        heading: article.heading,
        pageMeta: {
            ...pageMeta,
            description: article.homepageTeaser,
            title: article.seoHead || undefined,
            meta: getNoIndexMeta(article.source, section, featureState),
        },
        socialMeta: {
            title: article.socialHead || article.homepageHead,
            description: article.socialTeaser || article.homepageTeaser,
        },
        hideHeading: true,
        noMetaTitleTemplate: false,
        pageType: publicationPageType,
        adTargeting,
        additionalPageProperties: {
            primaryTopic: article.topics.primary,
            secondaryTopics: article.topics.secondary,
            publicationKind: article.kind,
            // DPT-1375 extra Metadata for variant testing. Remove when finished
            requiredAccess: article.requiredAccess,
            layout: article.layoutView,
        },
        section,
        compositions:
            renderTarget === 'amp'
                ? [getAmpArticleComposition(article, section, config)]
                : [
                      layout.composition({
                          type: 'box',
                          props: {
                              containerWidth:
                                  metrics.thewest.siteMetrics.mainContentWidth,
                              verticalGutters: ['outerMargin', 'unset'],
                          },
                          contentAreas: {
                              main: [
                                  layout.component({
                                      type: 'ad-unit',
                                      feature: 'outstream',
                                      props: {
                                          noticePosition: 'below-center',
                                          hiddenUntilLoaded: false,
                                          padding: adPaddingOptions['thewest'],
                                          slot: {
                                              id: 'outstream2x2',
                                              size: 'outstream',
                                          },
                                          adType: 'inline',
                                          disableBreachAds: disableBreachAds,
                                      },
                                  }),
                                  ...getBreakingNewsStrap(section),
                                  ...getElectionHeader(
                                      article,
                                      enableElectionNavigation,
                                      enableElectionSectionHeadToHead,
                                      enableElectionSectionHeadToHeadCountdown,
                                      electionDefinition,
                                  ),
                              ],
                          },
                      }),
                      layout.composition({
                          type: 'athena',
                          props: {
                              containerWidth:
                                  metrics.thewest.siteMetrics.mainContentWidth,
                              sidebarOptions: 'hiddenMobile',
                              elementType: 'article',
                              sidebarHeightLimitElementId:
                                  'taboola-below-article-thumbnails',
                              hideSidebar: getShouldHideArticleSidebar(),
                          },
                          contentAreas: {
                              sidebar: [
                                  ...getSidebar(
                                      article,
                                      section,
                                      disableBreachAds,
                                  ),
                              ],

                              main: [
                                  layout.component({
                                      type: 'topic-list',
                                      props: {
                                          topics: article.topics,
                                          verticalSpacing: 'sm',
                                      },
                                  }),

                                  layout.component({
                                      type: 'article',
                                      props: {
                                          meta: {
                                              slug: article.slug,
                                              kind: 'article',
                                              section: section,
                                              inlinePublicationContent:
                                                  section === 'genwest'
                                                      ? []
                                                      : [
                                                            {
                                                                kind: 'the-west-live-in-article-banner',
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'inline-queen-tribute-banner',
                                                                props: {
                                                                    heading:
                                                                        'Share your personal tribute to \n Queen Elizabeth II',
                                                                    subText:
                                                                        'Honour Her Majesty ahead of her final farewell.',
                                                                    buttonText:
                                                                        'Find out more',
                                                                    color: '#640C7C',
                                                                    driverLink:
                                                                        '/news/queen-elizabeth-ii/tributes',
                                                                    feature:
                                                                        'queens-tribute-in-article-banners',
                                                                },
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'inline-courts-n-crimes-banner',
                                                                props: {
                                                                    heading: `WA's biggest courts and crime stories to your inbox`,
                                                                    subText:
                                                                        'Sign-up to our weekly newsletter for free',
                                                                    buttonText:
                                                                        'Sign up',
                                                                    color: tokens
                                                                        .thewest
                                                                        .colors
                                                                        .brand,
                                                                    driverLink:
                                                                        'https://cloud.mail.wanews.com.au/thewest_newsletter',
                                                                    feature:
                                                                        'courts-n-crimes-in-article-banners',
                                                                },
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'inline-readers-survey-banner',
                                                                props: {
                                                                    displayCondition:
                                                                        true,
                                                                    visiblility:
                                                                        'visible',
                                                                    ...RegionalReaderSurvey,
                                                                    feature:
                                                                        'regional-readers-survey',
                                                                },
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'sport-call-to-action',
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'sports-hub-call-to-action',
                                                                insertAfter: 3,
                                                            },
                                                            {
                                                                kind: 'inline-the-game-banner',
                                                                insertAfter: 10,
                                                                props: {
                                                                    url: 'https://thegame.com.au/?utm_source=Acquisition&utm_medium=In+Article+Banner&utm_campaign=AFL+2026&utm_term=Major+Prize&utm_content=TWA',
                                                                    toggle: enableTheGameDriver,
                                                                },
                                                            },
                                                            {
                                                                kind: 'oovvuu-featured-card',
                                                                insertAfter: 7,
                                                                props: {
                                                                    oovvuuUrl:
                                                                        config.oovvuuUrl,
                                                                    oovvuuPath:
                                                                        config.oovvuuPath,
                                                                },
                                                            },
                                                            {
                                                                kind: 'inline-nightly-promo-banner',
                                                                insertAfter: 5,
                                                                toggle: enableNightlyPromoBanner,
                                                            },
                                                            {
                                                                kind: 'inline-olympics-medal-widget',
                                                                insertAfter: 11,
                                                                toggle: enableOlympicsMedalTallyWidget,
                                                                content: [{}],
                                                            },
                                                            {
                                                                kind: 'inline-pollie-rater-banner',
                                                                insertAfter: 8,
                                                                toggle: enablePollyRaterBanner,
                                                            },
                                                            {
                                                                kind: 'inline-federal-election-banner',
                                                                insertAfter: 6,
                                                            },
                                                            ...extraInlineArticleAds,
                                                            ...(article.kind ===
                                                            'event'
                                                                ? getInlineEventAds(
                                                                      Product.TheWest,
                                                                  )
                                                                : []),
                                                        ],
                                          },
                                          dataDefinitionArgs: {
                                              publicationId: article.id,
                                          },
                                          section,
                                          isVideoStickyEnabled: true,
                                      },
                                  }),
                                  ...(getShouldHideArticleSidebar()
                                      ? []
                                      : [
                                            layout.component({
                                                type: 'content-separator',
                                                props: {
                                                    verticalBottomSpacing: 'lg',
                                                },
                                            }),
                                        ]),
                                  layout.nestedComposition({
                                      type: 'box',
                                      props: {
                                          verticalSpacing: 'lg',
                                      },
                                      contentAreas: {
                                          main: [
                                              layout.component({
                                                  type: 'coral',
                                                  feature: 'comments',
                                                  props: {
                                                      storyID: getCoralStoryId(
                                                          section,
                                                          config.publicHostname,
                                                          article.id,
                                                      ),
                                                      articleCommentsEnabled:
                                                          article.allowCommenting,
                                                  },
                                              }),
                                          ],
                                      },
                                  }),

                                  layout.nestedComposition({
                                      type: 'janus',
                                      props: {
                                          verticalSpacing: 'sm',
                                          stretchColumns: true,
                                          layoutRatio: '2:3',
                                          breakpoints: {
                                              layoutRatiosBreakpoint: 'xs',
                                          },
                                          disableBreachAds: disableBreachAds,
                                      },
                                      contentAreas: {
                                          left: [
                                              layout.component({
                                                  type: 'curated-promotional-card',
                                                  props: {
                                                      fontScale: 1,
                                                      verticalSpacing: 'lg',
                                                      fixedRatios: {
                                                          default: ['4:3'],
                                                          md: '16:9',
                                                      },
                                                      hasBackground: true,
                                                      hasPadding: true,
                                                      mediaMode: 'edgeToEdge',
                                                      showCTA: true,

                                                      dataDefinitionArgs: {
                                                          type: 'active-promotion',
                                                          promotionSlotId:
                                                              primaryTopicId,
                                                          metadataKey:
                                                              promoCardMetadataKey,
                                                      },
                                                  },
                                              }),
                                          ],
                                          right: [
                                              ...relatedContent(
                                                  article,
                                                  section,
                                              ),
                                          ],
                                      },
                                  }),

                                  layout.nestedComposition({
                                      type: 'janus',
                                      feature: 'ripple',
                                      props: {
                                          stretchColumns: true,
                                          layoutRatio: '1:1',
                                          disableBreachAds: disableBreachAds,
                                      },
                                      contentAreas: {
                                          left: [
                                              layout.component({
                                                  type: 'ripple',
                                                  props: {
                                                      pageSection:
                                                          getRippleSectionTargeting(
                                                              section,
                                                              article,
                                                          ),
                                                  },
                                              }),
                                          ],

                                          right: [
                                              layout.component({
                                                  type: 'ripple',
                                                  props: {
                                                      pageSection:
                                                          getRippleSectionTargeting(
                                                              section,
                                                              article,
                                                          ),
                                                  },
                                              }),
                                          ],
                                      },
                                  }),
                                  ...articleFollowers(section),
                                  ...taboola(
                                      article,
                                      section,
                                      disableBreachAds,
                                  ),
                              ],
                          },
                      }),
                  ],
    }
}

const isGenWestArticle = (article: ArticleLikeV4DTO) => {
    if (
        article.topics.primary.id === 'genwest' ||
        article.topics.primary.parent?.id === 'genwest' ||
        (article.topics.secondary &&
            article.topics.secondary.findIndex(
                (topic) => topic.parent?.id === 'genwest',
            ) !== -1)
    ) {
        return true
    }

    return false
}

const taboola = (
    article: ArticleLikeV4DTO,
    section: TheWestSection,
    disableBreachAds?: boolean,
) => {
    return [
        isGenWestArticle(article)
            ? layout.component({
                  type: 'taboola',
                  feature: 'genwest-taboola',
                  props: {
                      taboolaContainerId: 'taboola-below-article-thumbnails',
                      locationType: 'article',
                      disableBreachAds: disableBreachAds,
                  },
              })
            : layout.component({
                  type: 'taboola',
                  feature:
                      section !== 'default' ? 'taboola-regionals' : 'taboola',
                  props: {
                      taboolaContainerId: 'taboola-below-article-thumbnails',
                      locationType: 'article',
                      disableBreachAds: disableBreachAds,
                  },
              }),
    ]
}

const relatedContent = (article: ArticleLikeV4DTO, section: TheWestSection) => {
    return [
        isGenWestArticle(article)
            ? layout.component({
                  type: 'related-content',
                  props: {
                      dataDefinitionArgs: {
                          type: 'curation',
                          name: 'genwest',
                          offset: 0,
                          pageSize: 6,
                      },
                  },
              })
            : layout.component({
                  type: 'publication-related-content',
                  props: {
                      dataDefinitionArgs: {
                          publicationId: article.id,
                      },
                  },
              }),
    ]
}

const getSidebarHeading = (section: TheWestSection) => {
    if (section === 'default' || section === 'genwest') {
        // the west
        return 'Top Stories'
    }

    // A regional masthead
    return 'Local News'
}

const getSidebar = (
    article: ArticleLikeV4DTO,
    section: TheWestSection,
    disableBreachAds?: boolean,
) => {
    if (section === 'genwest') {
        return [
            layout.component({
                type: 'ad-unit',
                feature: 'genwest-ad-slots',
                props: {
                    noticePosition: 'below-center',
                    padding: [0, 0, metrics.thewest.margins.md, 0],
                    slot: {
                        id: 'sidebar-top',
                        size: 'desktopMrecHalfPage',
                        pageType: publicationAdPageType[article.kind],
                    },
                    adType: 'inline',
                    disableBreachAds: disableBreachAds,
                },
            }),
            layout.nestedComposition({
                type: 'box',
                props: {
                    stickyOffset: 350,
                    fillContainer: true,
                },
                contentAreas: {
                    main: [
                        layout.component({
                            type: 'ad-unit',
                            feature: 'genwest-ad-slots',
                            props: {
                                noticePosition: 'below-center',
                                padding: [0, 0, metrics.thewest.margins.md, 0],
                                slot: {
                                    id: 'sidebar-middle',
                                    size: 'desktopMrec',
                                    pageType:
                                        publicationAdPageType[article.kind],
                                },
                                adType: 'inline',
                                disableBreachAds: disableBreachAds,
                            },
                        }),

                        layout.component({
                            type: 'juliet',
                            props: {
                                cardType: {
                                    type: 'landscape',
                                    format: 'landscape-smallImage',
                                },
                                removeHorizontalGutters: true,
                                kickerMode: 'hidden',
                                contentIsTangential: true,
                                sectionHeader: {
                                    heading: getSidebarHeading(section),
                                },
                                dataDefinitionArgs: getSidebarDataDefinition(
                                    section,
                                    article,
                                ),
                            },
                        }),
                    ],
                },
            }),
        ]
    }

    return [
        layout.component({
            type: 'ad-unit',
            props: {
                noticePosition: 'below-center',
                padding: [0, 0, metrics.thewest.margins.md, 0],
                slot: {
                    id: 'sidebar-top',
                    size: 'desktopMrecHalfPage',
                    pageType: publicationAdPageType[article.kind],
                },
                adType: 'inline',
                disableBreachAds: disableBreachAds,
            },
        }),
        layout.nestedComposition({
            type: 'box',
            props: {
                stickyOffset: 350,
                fillContainer: true,
            },
            contentAreas: {
                main: [
                    layout.component({
                        type: 'ad-unit',
                        props: {
                            noticePosition: 'below-center',
                            padding: [0, 0, metrics.thewest.margins.md, 0],
                            slot: {
                                id: 'sidebar-middle',
                                size: 'desktopMrec',
                                pageType: publicationAdPageType[article.kind],
                            },
                            adType: 'inline',
                            disableBreachAds: disableBreachAds,
                        },
                    }),

                    layout.component({
                        type: 'juliet',
                        props: {
                            cardType: {
                                type: 'landscape',
                                format: 'landscape-smallImage',
                            },
                            removeHorizontalGutters: true,
                            kickerMode: 'hidden',
                            contentIsTangential: true,
                            sectionHeader: {
                                heading: getSidebarHeading(section),
                            },
                            dataDefinitionArgs: getSidebarDataDefinition(
                                section,
                                article,
                            ),
                        },
                    }),
                ],
            },
        }),
    ]
}

const articleFollowers = (section: TheWestSection) => {
    // Is this the west of a regional masthead?
    const isTheWest = section === 'default'

    const components = []

    if (isTheWest) {
        components.push(
            layout.component({
                feature: 'newsletter-subscription-banner',
                type: 'newsletter-signup',
                props: {
                    text: 'The latest from The West delivered to your inbox daily.',
                    buttonLabel: 'Sign Up',
                    mode: 'default',
                },
            }),
        )
    }

    return components
}

const getRippleSectionTargeting = (
    section: TheWestSection,
    article: ArticleLikeV4DTO,
) => {
    if (section !== 'default') {
        return section
    } else {
        return article.topics.primary.parent
            ? article.topics.primary.parent.id
            : article.topics.primary.id
    }
}

const getSidebarDataDefinition = (
    section: TheWestSection,
    article: ArticleLikeV4DTO,
): ContentDataTypes => {
    if (isWestRegionalSection(section)) {
        const regionalConfig =
            regionalSiteConfig[section as TheWestRegionalSections]
        if (regionalConfig) {
            return {
                type: 'listing',
                topics: [regionalConfig.mastheadTopic],
                paging: {
                    page: 1,
                    pageSize: 3,
                },
            }
        }
    }

    return {
        type: 'curation',
        name: isGenWestArticle(article) ? 'genwest' : 'home',
        offset: 0,
        pageSize: 3,
    }
}

const getBreakingNewsStrap = (section: TheWestSection) => {
    if (isWestRegionalSection(section) || section === 'genwest') {
        // For now at least we dont want the breaking news strap on any regional masthead or genwest pages
        return []
    }

    return [
        layout.component({
            type: 'breaking-news',
            props: {
                dataDefinitionArgs: {
                    type: 'curation',
                    name: 'breaking-news',
                    offset: 0,
                    pageSize: 1,
                },
            },
        }),
        layout.component({
            type: 'breaking-news-promo-strap',
            props: {
                dataDefinitionArgs: {
                    type: 'curation',
                    name: 'breaking-news-promo-strap',
                    offset: 0,
                    pageSize: 1,
                },
            },
        }),
    ]
}

const getElectionHeader = (
    article: ArticleLikeV4DTO,
    enableElectionNavigation: string | boolean | undefined,
    enableElectionSectionHeadToHead: string | boolean | undefined,
    enableElectionSectionHeadToHeadCountdown: string | boolean | undefined,
    electionDefinition: ElectionDefinition,
) => {
    const { electionId } = electionDefinition
    const electionTopic = `politics/${electionId}`

    if (
        !enableElectionNavigation ||
        (article.topics.primary.id !== electionTopic &&
            !article.topics.secondary.some(
                (topic) => topic.id === electionTopic,
            ))
    ) {
        return []
    }

    return [
        layout.nestedComposition({
            type: 'box',
            props: {
                verticalSpacing: 'unset',
                breakpoints: {
                    lg: {
                        verticalSpacing: 'unset',
                        horizontalGuttersOverride: 15,
                    },
                },
            },
            contentAreas: {
                main: [
                    ...(enableElectionSectionHeadToHead
                        ? [
                              layout.component({
                                  type: 'election-head-to-head-seat-count-widget',
                                  props: {
                                      electionDefinition,
                                      countdownEnabled:
                                          enableElectionSectionHeadToHeadCountdown as boolean,
                                  },
                              }),
                          ]
                        : []),
                    layout.component({
                        type: 'election-navigation',
                        props: {
                            electionDefinition,
                        },
                    }),
                ],
            },
        }),
    ]
}
